import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Trans } from '@lingui/react/macro'

import type { PhotosApi } from '@jotta/types/PhotosApi'
import { Avatar } from '@jotta/ui/Avatar'
import SvgFillPlayPreview from '@jotta/ui/SvgFillPlayPreview'
import { cn } from '@jotta/utils/css'

import type { AlbumInfoStore } from '../../../store/AlbumInfoStore'

import { AlbumMeta } from './AlbumMeta'

function VideoOverlay({
  photo,
  style,
}: {
  photo: PhotosApi.Photo | undefined
  style?: CSSProperties
}) {
  return (
    photo?.duration && (
      <SvgFillPlayPreview
        className="pointer-events-none absolute left-1/2 top-1/2 h-1/4 -translate-x-1/2 -translate-y-1/2"
        style={style}
      />
    )
  )
}

export const SharedPhotosInfo = observer<{
  store: AlbumInfoStore
  goBig: boolean
  hasMorePhotos: boolean
  captureDate: string
  photoCount: number
}>(function SharedPhotosInfo({
  store,
  goBig,
  hasMorePhotos,
  captureDate,
  photoCount,
}) {
  const { model: album, photos } = store
  const { coverPhoto, shareInfo, ownerAvatar } = album
  const shareOwnerName = shareInfo?.ownerFullName

  const navigate = useNavigate()

  const photoStack = [
    photos
      .filter(value => value.id !== coverPhoto?.id)
      .slice(0, 1)
      .at(0),
    coverPhoto,
  ].filter(Boolean)

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4 md:grid-cols-[400px_auto] md:gap-8',
        {
          'md:grid-cols-[65fr_35fr]': goBig,
        },
      )}
    >
      <div className="md:pt-16">
        {ownerAvatar && (
          <Avatar
            className="border border-solid border-surface-container-low"
            avatar={ownerAvatar}
          />
        )}
        <header className="h2 py-2">
          <Trans>Shared by {shareOwnerName}</Trans>
        </header>

        <AlbumMeta
          hasMorePhotos={hasMorePhotos}
          captureDate={captureDate}
          photoCount={photoCount}
        />
      </div>

      <div className="relative aspect-[4/3] w-full md:order-first">
        {photoStack.map((photo, index) => (
          <img
            key={photo?.id}
            className={cn(
              'absolute top-0 size-full cursor-pointer rounded-lg object-cover',
            )}
            style={{
              rotate: `${index * -3}deg`,
            }}
            src={`${photo?.thumbnail_url}.l`}
            onClick={() => navigate(`./${photo?.id}`)}
          />
        ))}
        <VideoOverlay
          photo={coverPhoto}
          style={{
            rotate: `${(photoStack.length - 1) * -3}deg`,
          }}
        />
      </div>
    </div>
  )
})
